import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { cloneDeep, isNil, isEmpty, isUndefined } from "lodash";
import moment from "moment";
// ANT COMPONENTS
import {
  ActionMenu,
  AlertBox,
  BasicModal,
  Button,
  DataTable,
} from "@life-line-lab/components-react";
import { DeleteOutlined, EditOutlined, LoadingOutlined, MoreOutlined } from "@ant-design/icons";
// SHARED COMPONENT
import DataTableFilter from "Components/shared/data-table-filter";
import ModalContent from "../../shared/modal-content/modal-content";
// helpers
import { encode } from "Helpers/index";
// types
import { DriverApprovalStatus, DriverCategoriesTabs, DriverStatus } from "Types/new_driver_types";
import { ModalTranslationContent, Option, OrgType, Statuses } from "Types/common.types";
// hooks
import { useSession } from "Hooks/index";
// scss
import "./driver-list.scss";
// json
import sampleData from "./driver-list.json";
import { GlobalFilterAtom, GlobalFilterAtomType } from "Recoil/global_filter.atom";
import { useRecoilState, useRecoilValue } from "recoil";
import { Spin } from "antd";

interface DriverList {
  canList: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  driverList: any[];
  tab?: number;
  companiesOption: Option[];
  onViewProfile?: (profileID: string) => void;
  onDeactivateDriver?: (profileID: string, toDelete: boolean, reason: string) => void;
  onApproveRejectDriver?: (profileID: string, status: Statuses, reason: string) => void;
  onFilter?: (searchInput: string) => void;
}

const DriverList: React.FC<DriverList> = ({
  canList,
  canView,
  canEdit,
  canDelete,
  driverList,
  tab,
  companiesOption,
  isLoading,
  onViewProfile,
  onDeactivateDriver,
  onApproveRejectDriver,
  onFilter,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { org } = useSession();
  const baseColumns: any = [
    {
      title: t("label.driverID").toLocaleUpperCase(),
      dataIndex: ["data", "code"],
      defaultSortOrder: "ascend",
      // sorter: (a, b) => a.data.code > b.data.code ? 1 : a.data.code < b.data.code ? -1 : 0,
      // check out --> (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort)
      render: (_: any, record: any) => {
        if (canView) {
          return <a className="clickable-id">{record.data.code || ""}</a>;
        }
        return <span>{record.data.code || ""}</span>;
      },
      onCell: (record, rowIndex) => {
        if (canView) {
          return {
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              onViewProfile && onViewProfile(record.profile._id);
            },
          };
        }
      },
    },
    {
      title: t("label.name").toLocaleUpperCase(),
      dataIndex: ["profile", "fullName"],
    },
    {
      title: t("label.mobileNo").toLocaleUpperCase(),
      dataIndex: ["profile", "primaryPhone", "fullNumber"],
    },
    {
      title: t("label.driverType").toLocaleUpperCase(),
      dataIndex: ["data", "riderType"],
    },
    {
      title: t("label.vehicleType").toLocaleUpperCase(),
      dataIndex: ["data", "vehicleType"],
    },
  ];
  const initialModal: any = {
    open: false,
    type: "approve",
    record: {},
  };
  const orgIsOwner = org.orgType === OrgType.OWNER;
  const orgIsLP = org.orgType === OrgType.LP;
  const orgIsCompany = org.orgType === OrgType.COMPANY;
  const orgIsCustomer = org.orgType === OrgType.CUSTOMER;
  const driverTypeOptions: Option[] = [
    {
      label: t("option.all"),
      value: "",
    },
    {
      label: t("label.internal").toUpperCase(),
      value: "INTERNAL",
    },
    {
      label: t("label.external").toUpperCase(),
      value: "EXTERNAL",
    },
  ];
  const vehicleTypeOptions: Option[] = [
    {
      label: t("option.all"),
      value: "",
    },
    {
      label: t("label.car"),
      value: "CAR",
    },
    {
      label: t("label.van"),
      value: "VAN",
    },
    {
      label: t("label.motorcycle"),
      value: "MOTORCYCLE",
    },
    {
      label: t("label.lorry"),
      value: "LORRY",
    },
  ];

  const [tableColumns, setTableColumns] = useState(baseColumns);
  const lpColumns = tableColumns.concat({
    title: t("label.company").toLocaleUpperCase(),
    dataIndex: ["data", "companyName"],
  });
  const [modal, setModal] = useState<any>(initialModal);
  const [globalFilterState] = useRecoilState(GlobalFilterAtom);
  const initialFilterInput: any = {
    driverType: "",
    vehicleType: "",
    companiesFilter: "",
    ancestorOrgIDs: globalFilterState?.selectedOrgIDs?.length
      ? globalFilterState?.selectedOrgIDs
      : null,
  };
  const [filter, setFilter] = useState<any>(initialFilterInput);

  const initialFilterSelection = [
    {
      label: t("label.filterByDriver"),
      key: "driverType",
      name: "driverType",
      placeholder: t("placeholder.filterByDriverType"),
      value: filter.driverType,
      options: driverTypeOptions,
    },
  ];
  const [filterSelection, setFilterSelection] = useState<any>(initialFilterSelection);

  // useEffect(() => {
  //   if (!globalFilterValue.companyIds) return;

  //   const filterObj = {
  //     companiesFilter:
  //       globalFilterValue.companyIds.length > 1 ? "" : globalFilterValue.companyIds[0],
  //   };
  //   setFilter({ ...filter, ...filterObj });
  // }, [globalFilterValue]);

  useEffect(() => {
    if (orgIsCompany) {
      handleAddVehicleTypeFilter();
    }
    if (orgIsLP && !isEmpty(companiesOption)) {
      // handleAddCompanyFilter();
      handleAddVehicleTypeFilter();
    }
  }, [companiesOption]);

  useEffect(() => {
    if (!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length) {
      setFilter({ ...filter, ancestorOrgIDs: globalFilterState?.selectedOrgIDs });
    }
  }, [globalFilterState]);

  useEffect(() => {
    switch (tab) {
      case 0:
      case 1:
      case 2:
      case 4:
        handleAddEditDeactivateMenu();
        break;
      case 3:
        handleUnapprovedTab();
        break;
      case 5:
        handleRejectedTab();
        break;
      default:
        handleAddDeactivatedAtColumn();
        break;
    }
  }, [tab]);

  useEffect(() => {
    if (onFilter) onFilter(filter);
  }, [filter]);

  const handleAddEditDeactivateMenu = () => {
    let clonedCol = cloneDeep(baseColumns);
    clonedCol.push({
      title: "",
      dataIndex: [],
      width: 29,
      render: (_, record) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.edit"),
              icon: <EditOutlined />,
              onClick: () => handleActionClick(record, "EDIT"),
            },
            {
              label: t("action.deactivate"),
              icon: <DeleteOutlined />,
              onClick: () => handleActionClick(record, DriverStatus.DEACTIVATE),
            },
          ]}
        />
      ),
    });

    if (canEdit && canDelete) setTableColumns(clonedCol);
  };

  const handleUnapprovedTab = () => {
    let clonedCol = cloneDeep(baseColumns);
    const newCol = [
      ...clonedCol,
      {
        title: t("label.createdAt"),
        dataIndex: ["createdAt"],
        defaultSortOrder: "descend",
        sorter: (a, b) => (a.createdAt > b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0),
        render: (_: any, record: any) => {
          return (
            <span>
              {record.createdAt ? moment(record.createdAt).format("DD-MM-YYYY HH:mm:ss") : "-"}
            </span>
          );
        },
      },
      {
        title: "",
        dataIndex: [],
        render: (_, record) => {
          if (canEdit && canDelete) {
            return (
              <div className="btn-actions-container">
                <Button
                  type="success"
                  label={t("action.approve")}
                  onClick={() => {
                    handleActionClick(record, DriverApprovalStatus.APPROVED);
                  }}
                  className="approve-btn"
                />
                <Button
                  type="danger"
                  label={t("action.reject")}
                  onClick={() => {
                    handleActionClick(record, DriverApprovalStatus.REJECTED);
                  }}
                />
              </div>
            );
          }
          return;
        },
      },
    ];
    setTableColumns(newCol);
  };

  const handleAddCompanyNameColumn = () => {
    const companyNameCol = {};
    setTableColumns((prevState) => {
      [...prevState, companyNameCol];
    });
  };

  const handleAddDeactivatedAtColumn = () => {
    const deactivatedAtCol = {
      title: t("label.deactivatedAt").toLocaleUpperCase(),
      dataIndex: ["data", "tempDeleteInfo", "tempDeletedOn"],
      defaultSortOrder: "ascend",
      sorter: (a, b) =>
        a.data.tempDeleteInfo.tempDeletedOn > b.data.tempDeleteInfo.tempDeletedOn
          ? 1
          : a.data.tempDeleteInfo.tempDeletedOn < b.data.tempDeleteInfo.tempDeletedOn
          ? -1
          : 0,
      render: (_: any, record: any) => {
        return (
          <React.Fragment>
            {moment(record?.data.tempDeleteInfo?.tempDeletedOn).format("DD-MM-YYYY HH:mm:ss")}
          </React.Fragment>
        );
      },
    };
    setTableColumns((prevState) => [...prevState, deactivatedAtCol]);
  };

  const handleRejectedTab = () => {
    let clonedCol = cloneDeep(baseColumns);
    const newCol = [
      ...clonedCol,
      {
        title: t("label.createdAt"),
        dataIndex: ["createdAt"],
        render: (_: any, record: any) => {
          return (
            <span>
              {record.createdAt ? moment(record.createdAt).format("DD-MM-YYYY HH:mm:ss") : "-"}
            </span>
          );
        },
      },
      {
        title: "",
        dataIndex: [],
        render: (_, record) => {
          if (canEdit) {
            return (
              <div className="btn-actions-container">
                <Button
                  type="success"
                  label={t("action.approve")}
                  onClick={() => {
                    handleActionClick(record, DriverApprovalStatus.APPROVED);
                  }}
                  className="approve-btn"
                />
              </div>
            );
          }
          return;
        },
      },
    ];
    setTableColumns(newCol);
  };

  const handleActionClick = (record, type) => {
    switch (type) {
      case "EDIT": {
        const driverID = encode(record.profileID);
        history.push(
          `/driver-management/driver/${DriverCategoriesTabs.DRIVER_INFO}/${driverID}/edit`,
        );
        break;
      }
      case DriverStatus.DEACTIVATE: {
        setModal({ open: true, type: DriverStatus.DEACTIVATE, record: record });
        break;
      }
      case DriverApprovalStatus.REJECTED: {
        setModal({ open: true, type: DriverApprovalStatus.REJECTED, record: record });
        break;
      }
      case DriverApprovalStatus.APPROVED: {
        setModal({ open: true, type: DriverApprovalStatus.APPROVED, record: record });
        break;
      }
    }
  };

  const handleAddCompanyFilter = () => {
    const companiesFilter = {
      key: "companiesFilter",
      name: "companiesFilter",
      label: t("label.filterByCompanies"),
      placeholder: t("placeholder.filterByCompanies"),
      value: filter?.companiesFilter,
      options: companiesOption
        ? [{ label: t("option.all"), value: "" }].concat(companiesOption)
        : [{ label: t("option.all"), value: "" }],
    };

    setFilterSelection((prevState) => [...prevState, companiesFilter]);
  };

  const handleAddVehicleTypeFilter = () => {
    const vehicleTypeFilter = {
      label: t("placeholder.filterByVehicleType"),
      key: "vehicleType",
      name: "vehicleType",
      placeholder: t("placeholder.filterByVehicleType"),
      value: filter?.vehicleType,
      options: vehicleTypeOptions ?? [],
    };
    setFilterSelection((prevState) => [...prevState, vehicleTypeFilter]);
  };

  const handleSearch = (searchInput: string) => {
    setFilter((prevFilter) => ({ ...prevFilter, orFields: searchInput }));
  };

  const handleFilterSelection = (selection) => {
    if (!onFilter) return;

    if (!isNil(selection)) {
      setFilter({ ...filter, ...selection });
    }
  };

  // ? write as a helper function
  const handleModalTranslation = (): ModalTranslationContent => {
    if (modal.type === DriverApprovalStatus.APPROVED) {
      return {
        title: t("label.confirmDriverApproval").toUpperCase(),
        sureTxt: t("txt.areYouSureApproveAccount"),
        contentToConfirm: t("txt.driverID").toUpperCase(),
        actionLabel: t("action.approve"),
      };
    } else if (modal.type === DriverApprovalStatus.REJECTED) {
      return {
        title: t("label.rejectDriver").toUpperCase(),
        sureTxt: t("txt.areYouSureRejectAccount"),
        contentToConfirm: t("txt.driverID").toUpperCase(),
        actionLabel: t("action.reject"),
      };
    } else if (modal.type === DriverStatus.DEACTIVATE) {
      return {
        title: t("label.deactivateDriver").toUpperCase(),
        sureTxt: t("txt.areYouSureDeactivateAccount"),
        contentToConfirm: t("txt.driverID").toUpperCase(),
        actionLabel: t("action.deactivate"),
      };
    } else if (modal.type === DriverStatus.ACTIVATE) {
      return {
        title: t("label.activateDriver").toUpperCase(),
        sureTxt: t("txt.areYouSureActivateAccount"),
        contentToConfirm: t("txt.driverID").toUpperCase(),
        actionLabel: t("action.activate"),
      };
    } else
      return {
        title: "-",
        sureTxt: "-",
        contentToConfirm: "-",
        actionLabel: "-",
      };
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  return (
    <div className="driver-list">
      <div className="po-header">
        <div className="filter-component">
          <DataTableFilter
            searchPlaceholder={t("label.search")}
            onSearch={(text: string) => {
              handleSearch(text);
            }}
            onFilter={(selection) => {
              handleFilterSelection(selection);
            }}
            renderFilterSelection={filterSelection}
          />
        </div>
      </div>
      {canList &&
        (isLoading ? (
          <div className="loading-container">
            <Spin size="large" indicator={<LoadingOutlined />} />
          </div>
        ) : (
          <DataTable
            rowKey={(record: any) => {
              return record.profileID;
            }}
            columns={orgIsLP ? lpColumns : tableColumns}
            dataSource={driverList}
          />
        ))}
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        <ModalContent
          modal={modal}
          onHandleModalTranslation={handleModalTranslation}
          onResetModal={handleResetModalState}
          onDeactivateOrDelete={onDeactivateDriver && onDeactivateDriver}
          onApproveOrReject={onApproveRejectDriver && onApproveRejectDriver}
        />
      </BasicModal>
    </div>
  );
};

export default DriverList;
